import api, {
    DISTRIBUTOR_CANDIDATES_TYPE,
    EXTENSION_FOLIO_TYPE,
    FOLIO_CATALOG_TYPE,
    GETTING_STARTED_TYPE,
    LIST
} from '../Api';
import { IActionResponse } from '../../dal/BaseDAL';
import { ICollectionResponse } from '../../models/ICollectionResponse';
import { IDistributorCandidate, IExtensionFolio } from '../../models/Extension';

// Authoring extension folios, plus the distributor picker's read model.
//
// These sit on `admin/extensions/folios`, deliberately apart from `extensions/folios`, which is about one
// organization's INSTALLED folios. Same word, different subject: one is the connector definition, the
// other is a tenant's connection to it.
//
// The list a caller gets back is already filtered by the server to what they may edit — their own folios,
// or everything for a global admin — so nothing here re-filters by ownership. The UI decides what to
// offer; the server decides what is allowed, and re-checks per record on every write.
const folioAdminApi = api.injectEndpoints({
    endpoints: (build) => ({
        getFolioCatalog: build.query<IExtensionFolio[], void>({
            query: () => 'admin/extensions/folios',
            providesTags: (result) =>
                result
                    ? [
                        ...result.map(f => ({ type: FOLIO_CATALOG_TYPE, id: f.id } as const)),
                        { type: FOLIO_CATALOG_TYPE, id: LIST },
                    ]
                    : [{ type: FOLIO_CATALOG_TYPE, id: LIST }],
        }),
        getCatalogFolio: build.query<IExtensionFolio, string>({
            query: (id) => `admin/extensions/folios/${encodeURIComponent(id)}`,
            providesTags: (_result, _error, id) => [{ type: FOLIO_CATALOG_TYPE, id }],
        }),
        // Upsert: the id is the author's own and carries meaning, so there is no separate create verb.
        // `create` says the caller believes the id is free, and turns an id already in use into a 409
        // rather than a silent overwrite of somebody else's connector.
        saveCatalogFolio: build.mutation<IActionResponse, { id: string, folio: IExtensionFolio, create?: boolean }>({
            query: (arg) => ({
                url: `admin/extensions/folios/${encodeURIComponent(arg.id)}${arg.create ? '?create=true' : ''}`,
                method: 'PUT',
                body: arg.folio,
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: FOLIO_CATALOG_TYPE, id: arg.id },
                { type: FOLIO_CATALOG_TYPE, id: LIST },
                // A saved folio can change what the picker offers and what its capabilities are called.
                { type: DISTRIBUTOR_CANDIDATES_TYPE, id: LIST },
            ],
        }),
        deleteCatalogFolio: build.mutation<IActionResponse, string>({
            query: (id) => ({
                url: `admin/extensions/folios/${encodeURIComponent(id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: FOLIO_CATALOG_TYPE, id },
                { type: FOLIO_CATALOG_TYPE, id: LIST },
                { type: DISTRIBUTOR_CANDIDATES_TYPE, id: LIST },
            ],
        }),
        // Entitlement tags already in use as a gate somewhere on the platform. Suggestions, not a
        // vocabulary: an organization's entitlements are free-form strings with no enumeration anywhere,
        // so this is "what exists", and a new tag can still be typed.
        getFolioEntitlementSuggestions: build.query<string[], void>({
            query: () => 'admin/extensions/folios/entitlements',
            providesTags: [{ type: FOLIO_CATALOG_TYPE, id: 'ENTITLEMENTS' }],
        }),
        // The picker's one call: which distributors can I connect, which have I done, and are they working.
        getDistributorCandidates: build.query<ICollectionResponse<IDistributorCandidate>, void>({
            query: () => 'extensions/folios/distributors',
            providesTags: [{ type: DISTRIBUTOR_CANDIDATES_TYPE, id: LIST }],
        }),
        // Creates the connection and records exactly what it should do, in one call. Distributors need
        // their own route: the CRM quick setup rejects any folio that is not a configured CRM.
        setupDistributorFolio: build.mutation<IActionResponse, { id: string, capabilities: string[] }>({
            query: (arg) => ({
                url: `extensions/folios/distributors/${encodeURIComponent(arg.id)}/setup`,
                method: 'POST',
                body: { capabilities: arg.capabilities },
            }),
            invalidatesTags: (_res, _err, arg) => ([
                { type: EXTENSION_FOLIO_TYPE, id: arg.id },
                { type: EXTENSION_FOLIO_TYPE, id: LIST },
                { type: DISTRIBUTOR_CANDIDATES_TYPE, id: LIST },
                // Connecting a distributor moves the checklist row.
                { type: GETTING_STARTED_TYPE, id: LIST },
            ]),
        }),
    }),
});

export const {
    useGetFolioCatalogQuery,
    useGetFolioEntitlementSuggestionsQuery,
    useGetCatalogFolioQuery,
    useSaveCatalogFolioMutation,
    useDeleteCatalogFolioMutation,
    useGetDistributorCandidatesQuery,
    useSetupDistributorFolioMutation,
} = folioAdminApi;
